import { buffers, eventChannel, END, EventChannel } from 'redux-saga';
import { select, take, put, call } from 'redux-saga/effects';
import { push as navigateTo, getLocation } from 'connected-react-router';
import i18n from 'i18next';
import { apiDelete, apiGet, apiPost } from './apiSagas';
import { confirmSaga } from './confirmSaga';
import {
    Actions, sqlSuccess, sqlFailure, cacheCliSuccess, cacheCliFailure,
    getUploadsRequest, getUploadsSuccess, getUploadsFailure,
    uploadProgress, uploadSuccess, uploadFailure,
    deleteUploadEarly, deleteUploadSuccess, deleteUploadFailure, sqlRequest, deleteUploadRequest, uploadRequest, cacheCliRequest,
} from 'actions/adminActions';
import { getUpload } from 'selectors/adminSelectors';
import { Upload } from 'shared/models';
import { toError } from 'shared/util/toError';

export function* sqlSaga() {
    while (true) {
        const action: ReturnType<typeof sqlRequest> = yield take(Actions.SQL_REQUEST);
        try {
            // Jump to the admin database page if we aren't there already
            // this lets us debug other parts of the site more easily
            // (e.g. by kicking off a SQL request and showing the results)
            const { pathname }: Location = yield select(getLocation as any);
            if (!pathname.startsWith('/admin/database')) {
                yield put(navigateTo('/admin/database'));
            }
            const results: any = (yield call(apiPost, `/api/admin/sql`, action.payload)) as unknown;
            if (results.err) {
                // This is the one place in the app where the error object is returned as success
                yield put(sqlFailure(JSON.stringify(results.err, null, '  ')));
            }
            else {
                yield put(sqlSuccess(results));
            }
        }
        catch (e) {
            const err = toError(e);
            yield put(sqlFailure(err.message));
        }
    }
}

export function* cacheCliSaga() {
    while (true) {
        const action: ReturnType<typeof cacheCliRequest> = yield take(Actions.CACHE_CLI_REQUEST);
        const { cacheId } = action.meta;
        try {
            // Jump to the admin cache page if we aren't there already
            // this lets us debug other parts of the site more easily
            // (e.g. by kicking off a cache request and showing the results)
            const { pathname }: Location = yield select(getLocation as any);
            if (!pathname.startsWith('/admin/cache')) {
                yield put(navigateTo('/admin/cache'));
            }
            const results: any = (yield call(apiPost, `/api/admin/cache/${cacheId}`, action.payload)) as unknown;
            if (results && results.err) {
                // This is the one place in the app where the error object is returned as success
                yield put(cacheCliFailure(JSON.stringify(results.err, null, '  '), { cacheId }));
            }
            else {
                yield put(cacheCliSuccess(results, { cacheId }));
            }
        }
        catch (e) {
            const err = toError(e);
            yield put(cacheCliFailure(err.message, { cacheId }));
        }
    }
}

export function* getUploadsSaga() {
    while (true) {
        yield take(Actions.GET_UPLOADS_REQUEST);
        try {
            const uploads: Upload[] = yield call(apiGet, '/api/admin/uploads');
            yield put(getUploadsSuccess(uploads));
        }
        catch (e) {
            const err = toError(e);
            yield put(getUploadsFailure(err));
        }
    }
}

const stripQuotesRegex = /^['"]*(.*?)['"]*$/;

function createUploadChannel(endpoint: string, file: File) {
    let etag: string;

    return eventChannel(emitter => {
        const xhr = new XMLHttpRequest();

        const onProgress = (e: ProgressEvent) => {
            if (e.lengthComputable) {
                const progress = e.loaded / e.total;
                emitter({ progress });
            }
        };
        const onFailure = (e?: ProgressEvent) => {
            emitter({ error: 'ERROR.UPLOAD_FAILED' });
            emitter(END);
        };

        // Delay starting the upload to give the opening animation (400ms) time to run
        setTimeout(() => {
            xhr.upload.addEventListener("progress", onProgress);
            xhr.upload.addEventListener("error", onFailure);
            xhr.upload.addEventListener("abort", onFailure);
            xhr.onreadystatechange = e => {
                const { readyState, status } = xhr;
                if (readyState === 2) { // HEADERS_RECEIVED
                    etag = xhr.getResponseHeader("ETag") || '...';
                    etag = stripQuotesRegex.exec(etag)![1];
                }
                else if (readyState === 4) { // DONE
                    if (status === 200) {
                        // Advance the progress bar to 100%
                        emitter({ progress: 1 });

                        // Delay the actual acknowledgement of completion so the
                        // user has time to visually process what just happened.
                        setTimeout(() => {
                            emitter({ success: etag });
                            emitter(END);
                        }, 500);
                    }
                    else {
                        onFailure();
                    }
                }
            };

            try {
                xhr.open("PUT", endpoint, true);
                xhr.setRequestHeader("Content-Type", file.type);
                xhr.send(file);
            }
            catch (err) {
                console.error(err);
                onFailure();
            }
        }, 400);

        return () => {
            xhr.upload.removeEventListener("progress", onProgress);
            xhr.upload.removeEventListener("error", onFailure);
            xhr.upload.removeEventListener("abort", onFailure);
            xhr.onreadystatechange = null;
            xhr.abort();
        };
    }, buffers.sliding<object>(2));
}

export function* uploadFilesSaga() {
    let url: string;
    while (true) {
        const action: ReturnType<typeof uploadRequest> = yield take(Actions.UPLOAD_REQUEST);
        const file = action.payload;
        try {
            // Tell the server that we want to upload a file, it will give us a URL
            url = yield call(apiPost, `/api/admin/uploads`, { filename: file.name, mimeType: file.type });
        }
        catch (e) {
            const err = toError(e);
            yield put(uploadFailure(err.message));
            continue;
        }

        // Upload the file to the specified URL
        const channel: EventChannel<object> = yield call(createUploadChannel, url, file);
        while (true) {
            const { progress = 0, error, success: etag } = yield take(channel);
            if (error) {
                yield put(uploadFailure(i18n.t(error)));
                break;
            }
            if (etag) {
                const upload = {
                    filename: file.name,
                    size: file.size,
                    etag,
                    created: new Date(),
                };
                yield put(uploadSuccess(upload));

                // If we were unable to retrieve the ETag (i.e. because the user's browser
                // doesn't support Access-Control-Expose-Headers header) then re-request
                // all the uploads to get the new ETag
                if (etag === '...') {
                    yield put(getUploadsRequest());
                }
                break;
            }
            yield put(uploadProgress(progress));
        }
    }
}

export function* deleteUploadSaga() {
    while (true) {
        const action: ReturnType<typeof deleteUploadRequest> = yield take(Actions.DELETE_UPLOAD_REQUEST);
        const filename = action.payload;

        const confirmed: boolean = yield call(confirmSaga, i18n.t('UPLOADS.DELETE_CONFIRMATION', { filename } as any));
        if (!confirmed) {
            continue;
        }

        const upload: Upload | null = yield select(getUpload, { filename });
        if (!upload) {
            continue;
        }
        yield put(deleteUploadEarly(filename));
        try {
            yield call(apiDelete, `/api/admin/uploads/${encodeURIComponent(filename)}`);
            yield put(deleteUploadSuccess(filename));
        }
        catch (e) {
            const err = toError(e);
            yield put(deleteUploadFailure(err, upload));
        }
    }
}
